import type { ComponentPropsWithoutRef, ForwardedRef } from "react";
import { forwardRef } from "react";
import { twMerge } from "tailwind-merge";

import { ErrorLabel, IconWrapper, Label } from "~/ui";

export type InputSize = "sm" | "base" | "lg";

interface InputProps extends ComponentPropsWithoutRef<"input"> {
  id: string;
  label?: string;
  labelClasses?: string;
  inputClasses?: string;
  containerClasses?: string;
  leftIcon?: JSX.Element | string;
  rightIcon?: JSX.Element | string;
  error?: string;
}

export const Input = forwardRef(
  (
    {
      id,
      label,
      labelClasses,
      inputClasses,
      className,
      leftIcon,
      rightIcon,
      error,
      ...props
    }: InputProps & ComponentPropsWithoutRef<"input">,
    ref: ForwardedRef<HTMLInputElement>,
  ) => {
    return (
      <div className={twMerge("flex w-fit flex-col items-start", className)}>
        {label && (
          <Label htmlFor={id} className={twMerge("mb-2", labelClasses)}>
            {label}
          </Label>
        )}

        <div className="relative flex w-full flex-row content-center items-center">
          {!!leftIcon && (
            <div className="pointer-events-none absolute pb-1 pl-2">
              <IconWrapper size="sm">{leftIcon}</IconWrapper>
            </div>
          )}
          <input
            ref={ref}
            type="text"
            id={id}
            className={twMerge(
              "w-96 flex-1 rounded-lg border border-neutral-variant-60 bg-surface-container-low px-4 py-3 text-base text-secondary-30 placeholder-neutral-variant-60 focus:border-primary-80/40 dark:bg-surface-container-low-dark dark:text-secondary dark:focus:text-primary-98 dark:active:text-primary-98",
              error && "border-error-70",
              leftIcon && "pl-8",
              rightIcon && "pr-8",
              inputClasses,
            )}
            {...props}
          />

          {!!rightIcon && (
            <div className="pointer-events-none absolute right-2">
              <IconWrapper size="sm">{rightIcon}</IconWrapper>
            </div>
          )}
        </div>
        {error && <ErrorLabel>{error}</ErrorLabel>}
      </div>
    );
  },
);
Input.displayName = "Input";
